import React, { useState, useMemo } from "react";
import { ChevronRight, ChevronLeft, ChevronDown } from "lucide-react";

const SortIcon = ({ column, sortBy, sortDesc }) => {
  if (sortBy !== column) {
    return (
      <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (sortDesc) {
    return (
      <svg className="w-4 h-4 ml-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4l9 16 9-16H3z" />
      </svg>
    );
  } else {
    return (
      <svg className="w-4 h-4 ml-1 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 20L12 4 3 20h18z" />
      </svg>
    );
  }
};

const CompletedLessonsTable = ({ lessons = [] }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDateRange, setSelectedDateRange] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("completionDate");
  const [sortDesc, setSortDesc] = useState(true);

  // Lesson type dropdown options
  const categoryOptions = [
    { label: "All Categories", value: "all" },
    { label: "Food", value: "food" },
    { label: "Wine", value: "wine" },
  ];

  const dateRangeOptions = [
    { label: "All Time", value: "all" },
    { label: "Last 7 Days", value: "7" },
    { label: "Last 30 Days", value: "30" },
    { label: "Last 90 Days", value: "90" },
  ];

  const pageSizeOptions = [10, 20, 50];

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(column);
      setSortDesc(true); // Default to descending for new sort
    }
  };

  // Pre-process lessons to include derived fields for sorting
  const processedLessons = useMemo(() => {
    return lessons.map(lesson => {
      const category =
        lesson.category ||
        (lesson.title?.toLowerCase().includes("food") ? "food" : "wine");

      const userProgress =
        lesson.progress && !Array.isArray(lesson.progress)
          ? lesson.progress
          : Array.isArray(lesson.progress)
            ? lesson.progress[0]
            : null;

      let timeSpentMinutes = 0;
      if (userProgress) {
        if (userProgress.timeSpent)
          timeSpentMinutes += Math.round(userProgress.timeSpent / 60);
        if (Array.isArray(userProgress.attempts)) {
          userProgress.attempts.forEach((a) => {
            if (a.timeSpent)
              timeSpentMinutes += Math.round(a.timeSpent / 60);
          });
        }
      }

      let improvementLabel = "-";
      let improvementValue = 0;
      if (
        userProgress &&
        Array.isArray(userProgress.attempts) &&
        userProgress.attempts.length >= 2
      ) {
        const first = userProgress.attempts[0]?.score || 0;
        const last =
          userProgress.attempts[userProgress.attempts.length - 1]?.score || 0;
        improvementValue = last - first;
        improvementLabel = `${improvementValue >= 0 ? "+" : ""}${improvementValue}%`;
      }

      return {
        ...lesson,
        derivedCategory: category,
        derivedTimeSpent: timeSpentMinutes,
        derivedImprovementValue: improvementValue,
        derivedImprovementLabel: improvementLabel,
        userProgress
      };
    });
  }, [lessons]);

  // Filtering & Sorting
  const filteredLessons = useMemo(() => {
    let filtered = processedLessons.filter((lesson) => {
      // Category Filter
      if (selectedCategory !== "all") {
        if (lesson.derivedCategory !== selectedCategory) return false;
      }

      // Date Range Filter
      if (selectedDateRange !== "all") {
        const dateStr = lesson.completedAt || lesson.completionDate;
        const lessonDate = new Date(dateStr);
        const daysAgo = parseInt(selectedDateRange);
        const dateLimit = new Date();
        dateLimit.setDate(dateLimit.getDate() - daysAgo);
        // Reset time to start of that day for accurate comparison
        dateLimit.setHours(0, 0, 0, 0);

        if (!lessonDate || isNaN(lessonDate.getTime()) || lessonDate < dateLimit) return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let valA, valB;

      switch (sortBy) {
        case "title":
          valA = a.title?.toLowerCase() || "";
          valB = b.title?.toLowerCase() || "";
          break;
        case "category":
          valA = a.derivedCategory || "";
          valB = b.derivedCategory || "";
          break;
        case "completionDate":
          valA = new Date(a.completedAt || a.completionDate);
          valB = new Date(b.completedAt || b.completionDate);
          break;
        case "score":
          valA = parseFloat(a.score) || 0;
          valB = parseFloat(b.score) || 0;
          break;
        case "attempts":
          valA = parseInt(a.attempts) || 0;
          valB = parseInt(b.attempts) || 0;
          break;
        case "timeSpent":
          valA = a.derivedTimeSpent;
          valB = b.derivedTimeSpent;
          break;
        case "improvement":
          valA = a.derivedImprovementValue;
          valB = b.derivedImprovementValue;
          break;
        default:
          valA = a[sortBy];
          valB = b[sortBy];
      }

      if (valA < valB) return sortDesc ? 1 : -1;
      if (valA > valB) return sortDesc ? -1 : 1;
      return 0;
    });

    return filtered;
  }, [processedLessons, selectedCategory, selectedDateRange, sortBy, sortDesc]);

  // Pagination
  const totalPages = Math.ceil(filteredLessons.length / pageSize);
  const paginatedLessons = filteredLessons.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="w-full text-left bg-white">
      {/* Table Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-lg font-black text-[#111827] ">
            Completed Lessons
          </h2>
          <p className="text-xs font-bold text-gray-400 mt-0.5">
            Review your learning history and performance
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400">Show:</span>
            <div className="relative group">
              <select
                className="appearance-none bg-[#F9F6F2] border-none rounded-lg px-3 py-1.5 pr-8 text-xs font-bold text-[#111827] focus:ring-0 cursor-pointer"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size} rows
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-focus-within:rotate-180 transition-transform"
              />
            </div>
          </div>

          <div className="relative group">
            <select
              className="appearance-none bg-[#F9F6F2] border-none rounded-lg px-4 py-1.5 pr-10 text-xs font-bold text-[#111827] focus:ring-0 cursor-pointer"
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
            >
              {dateRangeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-focus-within:rotate-180 transition-transform"
            />
          </div>

          <div className="relative group">
            <select
              className="appearance-none bg-[#F9F6F2] border-none rounded-lg px-4 py-1.5 pr-10 text-xs font-bold text-[#111827] focus:ring-0 cursor-pointer"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={12}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-focus-within:rotate-180 transition-transform"
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto -mx-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#FAF5F1] border-y border-gray-100">
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("title")}
              >
                <div className="flex items-center">
                  Chapter Title <SortIcon column="title" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("category")}
              >
                <div className="flex items-center">
                  Category <SortIcon column="category" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider">
                <div className="flex items-center gap-1 cursor-default">
                  Unit
                </div>
              </th>
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("completionDate")}
              >
                <div className="flex items-center">
                  Completion Date <SortIcon column="completionDate" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("score")}
              >
                <div className="flex items-center">
                  Score <SortIcon column="score" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("attempts")}
              >
                <div className="flex items-center">
                  Attempts <SortIcon column="attempts" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th
                className="px-6 py-3.5 text-left text-[12px] font-bold text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                onClick={() => handleSort("timeSpent")}
              >
                <div className="flex items-center">
                  Time Spent <SortIcon column="timeSpent" sortBy={sortBy} sortDesc={sortDesc} />
                </div>
              </th>
              <th className="px-6 py-3.5 text-[12px] font-bold text-gray-500 tracking-wider text-right">
                <div className="flex items-center justify-end gap-1 cursor-pointer">
                  Improvement
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 font-semibold">
            {paginatedLessons.map((lesson, idx) => {
              const category = lesson.derivedCategory;

              // real chapter name if available
              const chapterLabel = lesson.chapter_name
                ? `Chapter ${lesson.chapter}: ${lesson.chapter_name}`
                : `Chapter ${lesson.chapter}`;

              return (
                <tr
                  key={lesson.uuid || idx}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-6 py-5">
                    <div className="text-sm font-bold text-[#111827] mb-1">
                      {lesson.title}
                    </div>
                    <span className="text-xs font-bold text-gray-500">
                      {" "}
                      {/* {lesson.unit} */}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${category === "food"
                        ? "bg-[#FCE7E8] text-[#881337]"
                        : "bg-[#F5E6DA] text-[#A67C52]"
                        }`}
                    >
                      {category === "food" ? "Food" : "Wine"}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-[11px] text-gray-500 font-bold whitespace-nowrap">
                    {/* {chapterLabel} */}
                    {lesson.unit}
                  </td>
                  <td className="px-6 py-5 text-[11px] text-gray-500 font-bold">
                    {lesson.completionDate}
                  </td>
                  <td className="px-6 py-5 text-[13px] font-black text-[#111827]">
                    {lesson.score}
                  </td>
                  <td className="px-6 py-5 text-[11px] text-gray-500 font-black">
                    {lesson.attempts}
                  </td>
                  <td className="px-6 py-5 text-[11px] text-gray-500 font-black">
                    {lesson.derivedTimeSpent}m
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex items-center justify-end gap-1 text-[11px] font-black text-[#137333]">
                      <span className="text-lg leading-none">↑</span>{" "}
                      <span>{lesson.derivedImprovementLabel}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-100">
        <p className="text-xs font-bold text-gray-400">
          Showing {paginatedLessons.length} of {filteredLessons.length}{" "}
          completed lessons
        </p>

        <div className="flex items-center gap-1.5">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            className="p-2 rounded-lg border border-gray-100 text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronLeft size={16} />
          </button>

          {[...Array(Math.min(totalPages, 5))].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${currentPage === i + 1
                ? "bg-[#881337] text-white shadow-md shadow-[#881337]/20"
                : "text-gray-500 hover:bg-gray-50 border border-transparent"
                }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            className="p-2 rounded-lg border border-gray-100 text-gray-400 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletedLessonsTable;
